import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  AppNotification,
  archiveNotification,
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  NotificationPersona,
} from '../services/notifications/notificationService';

export function useNotifications(persona: NotificationPersona) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const token = session?.access_token;
  const listQueryKey = ['notifications', persona, 'list'];
  const unreadQueryKey = ['notifications', persona, 'unread-count'];

  const listQuery = useQuery({
    queryKey: listQueryKey,
    queryFn: () => fetchNotifications(token!, persona),
    enabled: Boolean(token),
  });

  const unreadQuery = useQuery({
    queryKey: unreadQueryKey,
    queryFn: () => fetchUnreadCount(token!, persona),
    enabled: Boolean(token),
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => markNotificationRead(token!, id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: listQueryKey });
      await queryClient.cancelQueries({ queryKey: unreadQueryKey });

      const previousItems = queryClient.getQueryData<AppNotification[]>(listQueryKey);
      const previousUnread = queryClient.getQueryData<number>(unreadQueryKey);

      queryClient.setQueryData<AppNotification[]>(listQueryKey, (current) =>
        (current ?? []).map((item) =>
          item.id === id && item.status === 'unread'
            ? { ...item, status: 'read', readAt: new Date().toISOString() }
            : item
        )
      );

      const wasUnread = previousItems?.some((item) => item.id === id && item.status === 'unread');
      if (wasUnread) {
        queryClient.setQueryData<number>(unreadQueryKey, (current) =>
          Math.max(0, (current ?? 0) - 1)
        );
      }

      return { previousItems, previousUnread };
    },
    onError: (_error, _id, context) => {
      if (context?.previousItems) {
        queryClient.setQueryData(listQueryKey, context.previousItems);
      }
      if (context?.previousUnread !== undefined) {
        queryClient.setQueryData(unreadQueryKey, context.previousUnread);
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: listQueryKey }),
        queryClient.invalidateQueries({ queryKey: unreadQueryKey }),
      ]);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => archiveNotification(token!, id),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: listQueryKey });
      await queryClient.cancelQueries({ queryKey: unreadQueryKey });

      const previousItems = queryClient.getQueryData<AppNotification[]>(listQueryKey);
      const previousUnread = queryClient.getQueryData<number>(unreadQueryKey);
      const removed = previousItems?.find((item) => item.id === id);

      queryClient.setQueryData<AppNotification[]>(listQueryKey, (current) =>
        (current ?? []).filter((item) => item.id !== id)
      );

      if (removed?.status === 'unread') {
        queryClient.setQueryData<number>(unreadQueryKey, (current) =>
          Math.max(0, (current ?? 0) - 1)
        );
      }

      return { previousItems, previousUnread };
    },
    onError: (_error, _id, context) => {
      if (context?.previousItems) {
        queryClient.setQueryData(listQueryKey, context.previousItems);
      }
      if (context?.previousUnread !== undefined) {
        queryClient.setQueryData(unreadQueryKey, context.previousUnread);
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: listQueryKey }),
        queryClient.invalidateQueries({ queryKey: unreadQueryKey }),
      ]);
    },
  });

  return {
    items: listQuery.data ?? [],
    unreadCount: unreadQuery.data ?? 0,
    isLoading: listQuery.isLoading || unreadQuery.isLoading,
    error: listQuery.error ?? unreadQuery.error,
    markRead: markReadMutation.mutateAsync,
    remove: removeMutation.mutateAsync,
    isMarkingRead: markReadMutation.isPending,
    isRemoving: removeMutation.isPending,
  };
}
